import { inject } from '@angular/core';
import { Route, Router } from '@angular/router';
import { environment as env } from '@env';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off

export const debugOnlyWhenSentry = () =>
    env.sentryEnabled && env.sentryDebugEnabled ? true : inject(Router).parseUrl('/');

export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'sign-in',
    },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'control-tower/dashboard',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import('app/modules/auth/confirmation-required/confirmation-required.routes'),
            },
            {
                path: 'forgot-password',
                loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
            },
            {
                path: 'reset-password',
                loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes'),
            },
            {
                path: 'sign-in',
                loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes'),
            },
        ],
    },

    // Sign-out must not sit behind AuthGuard — clearing the session would block this route.
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'unlock-session',
                loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes'),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () => import('app/modules/landing/home/home.routes'),
            },
        ],
    },

    // Public external routes (supplier quote links from enquiry emails)
    {
        path: 'econ',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/admin/standalone/standalone.routes'),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            //Control Tower
            {
                path: 'control-tower',
                loadChildren: () => import('app/modules/admin/control-tower/control-tower.routes'),
                // children: [
                //     {
                //         path: 'dashboard',
                //         loadChildren: () =>
                //             // import('app/modules/admin/control-tower/ct-dashboard/ct-dashboard.routes'),
                //         import('app/modules/admin/control-tower/control-tower.routes'),
                //     },
                // ],
            },
            // {
            //     path: 'purchase',
            //     children: [
            //         // Order
            //         {
            //             path: 'order',
            //             loadChildren: () => import('app/modules/admin/purchases/purchases.routes'),
            //         },
            //     ],
            // },
            {
                path: 'purchase',
                loadChildren: () => import('app/modules/admin/purchases/purchases.routes'),
            },
            // Level 2 approval email links (signed-in users only; the server checks the approver)
            {
                path: 'prepay-approval/:token',
                loadComponent: () =>
                    import('app/modules/admin/purchases/vendorPrePayment/prepay-approval/prepay-approval.component').then(
                        (m) => m.PrepayApprovalComponent
                    ),
            },
            {
                path: 'company',
                loadChildren: () => import('app/modules/admin/company/company.routes'),
            },
            {
                path: 'hr',
                loadChildren: () => import('app/modules/admin/hr/hr.routes'),
            },
            {
                path: 'sales',
                loadChildren: () => import('app/modules/admin/sales/sales.routes'),
            },
            {
                path: 'claims',
                loadChildren: () => import('app/modules/admin/claims/claims.routes'),
            },
            {
                path: 'settings',
                loadChildren: () => import('app/modules/admin/settings/settings.routes'),
            },
            {
                path: 'income',
                loadChildren: () => import('app/modules/admin/income/income.routes'),
            },
            {
                path: 'expense',
                loadChildren: () => import('app/modules/admin/expense/expense.routes'),
            },
            {
                path: 'vendors',
                loadChildren: () => import('app/modules/admin/vendors/vendor.routes'),
            },
            {
                path: 'customer',
                loadChildren: () => import('app/modules/admin/customer/customer.routes'),
            },
            {
                path: 'masters',
                loadChildren: () => import('app/modules/admin/masters/masters.routes'),
            },
            {
                path: 'user',
                loadChildren: () => import('app/modules/admin/user/user.routes'),
            },
            {
                path: 'email-manager',
                loadChildren: () => import('app/modules/admin/email-manager/email-manager.routes'),
            },
            {
                path: 'mail-automation',
                loadChildren: () => import('app/modules/admin/mail-automation/mail-automation.routes'),
            },
            {
                path: 'reports',
                loadChildren: () => import('app/modules/admin/reports/reports.routes'),
            },
            {
                path: 'accounting',
                loadChildren: () => import('app/modules/admin/accounting/accounts.routes'),
            },
            {
                path: 'master-data-config',
                loadChildren: () => import('app/modules/admin/master-data-config/master-data-config.routes'),
            },
        ],
    },

    // Sentry test path
    ...(env.sentryEnabled && env.sentryDebugEnabled
        ? [
              {
                  path: 'debug/sentry',
                  loadComponent: () =>
                      import('@app/debug-sentry.component').then((m) => m.DebugSentryComponent),
              },
          ]
        : []),
];
